import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, forwardRef } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, interval, merge } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { Permission } from '@core/interfaces';
import { EnvironmentService, EventService, NotificationService } from '@core/services';
import { dialogAttributes } from '@util/dialog';

import { endpoints } from '@environment';

import {
  AuthPermissionsResponseBody,
  AuthPingResponseBody,
  ChangeCustomerResponseBody,
  ChangeFacilityResponseBody,
  ChangeLanguageResponseBody,
  ChangeThemeResponseBody,
  LoginResponseBody,
  LogoutResponseBody,
  UpdateSessionResponseBody,
} from '#types/auth';

import { TimeoutComponent } from '@auth/components/timeout/timeout.component';
import { CONSTANTS } from '@auth/constants';
import { AuthActions, fromAuth } from '@auth/state';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loginEvent$ = new Subject<void>();

  loginRequestLoading$ = new Subject<boolean>();
  logoffRequestLoading$ = new Subject<boolean>();

  loginError$ = new Subject<HttpErrorResponse>();
  logoffError$ = new Subject<HttpErrorResponse>();
  changeFacilityError$ = new Subject<HttpErrorResponse>();

  changeCustomerRequestLoading$ = new Subject<boolean>();
  changeFacilityRequestLoading$ = new Subject<boolean>();
  updateSessionRequestLoading$ = new Subject<boolean>();
  changeLanguageRequestLoading$ = new Subject<boolean>();
  changeThemeRequestLoading$ = new Subject<boolean>();
  permissionsRequestLoading$ = new Subject<boolean>();

  state$ = this.store.select(fromAuth.selectAuthState);

  authenticated$ = this.store.select(fromAuth.selectAuthenticated);
  sessionTimeout$ = this.store.select(fromAuth.selectSessionTimeout);
  sessionTimeoutTime$ = this.store.select(fromAuth.selectSessionTimeoutTime);

  changePending$ = this.store.select(fromAuth.selectChangePending);
  screens$ = this.store.select(fromAuth.selectScreens);

  currentScreen$ = this.store.select(fromAuth.selectCurrentScreen);
  distinctPreviousScreen$ = this.store.select(fromAuth.selectDistinctPreviousScreen);
  previousScreen$ = this.store.select(fromAuth.selectPreviousScreen);

  custid$ = this.store.select(fromAuth.selectCustid);
  env$ = this.store.select(fromAuth.selectEnv);
  facility$ = this.store.select(fromAuth.selectFacility);
  groupid$ = this.store.select(fromAuth.selectGroupid);
  language$ = this.store.select(fromAuth.selectLanguage);
  nameid$ = this.store.select(fromAuth.selectNameid);
  user$ = this.store.select(fromAuth.selectUser);

  app$ = this.store.select(fromAuth.selectApp);
  appEnv$ = this.store.select(fromAuth.selectAppEnv);

  allcusts$ = this.store.select(fromAuth.selectAllcusts);
  chgfacility$ = this.store.select(fromAuth.selectChgfacility);

  permissions$ = this.store.select(fromAuth.selectPermissions);

  hasPassThru$ = this.store.select(fromAuth.selectHasPassThru);

  id$ = this.store.select(fromAuth.selectId);

  lastLogin$ = new BehaviorSubject<number>(0);
  lastActivity$ = new BehaviorSubject<number>(0);
  displayChangeLanguage$ = new BehaviorSubject<boolean>(false);

  theme$ = this.store.select(fromAuth.selectTheme);

  nameid: string = 'Username';
  groupid: string;
  facility: string;
  custid: string;
  permissions: Permission[] = [];
  allcusts: string;

  chgFacility: string;

  appEnv: string;
  userEnv: string;

  authenticated: boolean = false;
  sessionTimeout: boolean = false;

  broadcastLeader: boolean = false;
  timeoutDialogOpen: boolean = false;

  accessDeniedData$ = new BehaviorSubject<{ url: string; permission: string }>(null);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private http: HttpClient,
    private store: Store,
    private eventService: EventService,
    private router: Router,
    private notificationService: NotificationService,
    @Inject(forwardRef(() => DialogService)) private dialogService: DialogService,
    private translocoService: TranslocoService,
    @Inject(DOCUMENT) private document: Document,
    private environmentService: EnvironmentService
  ) {
    this.theme$.pipe(take(1)).subscribe((theme: string) => {
      this.setTheme(theme);
    });

    this.permissions$.pipe(filter((permissions) => !!permissions)).subscribe((permissions) => {
      this.permissions = permissions;
    });

    this.nameid$.pipe(filter((nameid) => !!nameid)).subscribe((nameid) => {
      this.nameid = nameid;
    });

    this.facility$.pipe().subscribe((facility) => {
      this.facility = facility;
    });

    this.groupid$.pipe().subscribe((groupid) => {
      this.groupid = groupid;
    });

    this.chgfacility$.pipe().subscribe((chgFacility) => {
      this.chgFacility = chgFacility;
    });

    this.allcusts$.pipe().subscribe((allcusts) => {
      this.allcusts = allcusts;
    });

    this.custid$.pipe().subscribe((custid) => {
      this.custid = custid;
    });

    this.authenticated$.pipe().subscribe((authenticated) => {
      this.authenticated = authenticated;
    });

    this.sessionTimeout$.pipe().subscribe((sessionTimeout) => {
      this.sessionTimeout = sessionTimeout;
    });

    this.timeoutDialogOpen = false;

    // send a ping to the endpoint once a minute if authenticated
    // initial interval
    interval(this.environmentService.settings.pingInterval)
      .pipe(
        takeUntil(
          merge(
            this.authenticated$.pipe(filter((value) => value === false)),
            this.sessionTimeout$.pipe(filter((value) => value === true))
          ).pipe(take(1))
        )
      )
      .subscribe(() => {
        if (this.broadcastLeader) {
          this.store.dispatch(AuthActions.SessionPing());
        }
      });

    // restart interval when login event is emitted
    this.loginEvent$.pipe().subscribe(() => {
      interval(this.environmentService.settings.pingInterval)
        .pipe(
          takeUntil(
            merge(
              this.authenticated$.pipe(filter((value) => value === false)),
              this.sessionTimeout$.pipe(filter((value) => value === true))
            ).pipe(take(1))
          )
        )
        .subscribe(() => {
          if (this.broadcastLeader) {
            this.store.dispatch(AuthActions.SessionPing());
          }
        });
    });

    // check for session timeout state on page load
    this.sessionTimeout$.pipe(withLatestFrom(this.authenticated$), take(1)).subscribe(([sessionTimeout, authenticated]) => {
      if (authenticated && sessionTimeout) {
        setTimeout(() => {
          this.timeoutDialog();
        }, 1500);
      }
    });

    // set application environment from the current URL
    const environment = this.document.defaultView.location.pathname.split('/')[1];

    if (environment === 'prod' || environment === 'test' || environment === 'dev') {
      this.store.dispatch(AuthActions.SetApplicationEnvironment({ environment }));
    }

    this.appEnv$.pipe(withLatestFrom(this.env$)).subscribe(([appEnv, env]) => {
      // ignore this check on localhost
      if (this.document.defaultView.location.hostname === 'localhost') {
        return;
      }

      if (appEnv && env && appEnv !== env) {
        this.notificationService.error({ message: this.translate('core.invalid_environment') });

        setTimeout(() => {
          void this.router.navigate(['login']);

          setTimeout(() => {
            localStorage.clear();
            sessionStorage.clear();
            this.store.dispatch(AuthActions.ResetState());
            window.location.reload();
          }, 50);
        }, 5000);
      }
    });
  }

  login(username: string, password: string) {
    return this.http.post(endpoints.auth.login, { username, password }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_LOGON),
      map((response: LoginResponseBody) => response)
    );
  }

  logout() {
    return this.http.post(endpoints.auth.logout, {}, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_LOGOFF),
      map((response: LogoutResponseBody) => response)
    );
  }

  changeCustomer(custid: string) {
    return this.http.post(endpoints.auth.changeCustomer, { custid }, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_CHANGE_CUSTOMER),
      map((response: ChangeCustomerResponseBody) => response)
    );
  }

  changeFacility(facility: string) {
    return this.http.post(endpoints.auth.changeFacility, { facility }, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_CHANGE_FACILITY),
      map((response: ChangeFacilityResponseBody) => response)
    );
  }

  updateSession(id: string) {
    return this.http.post(endpoints.auth.updateSession, { id }, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_UPDATE_SESSION),
      map((response: UpdateSessionResponseBody) => response)
    );
  }

  changeLanguage(language: string) {
    return this.http.post(endpoints.auth.changeLanguage, { language }, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_CHANGE_LANGUAGE),
      map((response: ChangeLanguageResponseBody) => response)
    );
  }

  changeTheme(theme: string) {
    return this.http.post(endpoints.auth.changeTheme, { theme }, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_CHANGE_THEME),
      map((response: ChangeThemeResponseBody) => response)
    );
  }

  getPermissions() {
    return this.http.post(endpoints.auth.permissions, {}, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_AUTH_GET_PERMISSIONS),
      map((response: AuthPermissionsResponseBody) => response)
    );
  }

  ping() {
    return this.http.post(endpoints.auth.ping, null, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_PING),
      map((response: AuthPingResponseBody) => response)
    );
  }

  setCustomer(custid: string) {
    this.store.dispatch(AuthActions.ChangeCustomer({ custid }));
  }

  setFacility(facility: string) {
    this.store.dispatch(AuthActions.ChangeFacility({ facility }));
  }

  setLanguage(language: string) {
    this.store.dispatch(AuthActions.ChangeLanguage({ language }));
  }

  changeLanguageDialog(display: boolean = true) {
    this.displayChangeLanguage$.next(display);
  }

  updateLastLogin(time = Math.floor(Date.now() / 1000)) {
    this.lastLogin$.next(time);

    if (time > 0) {
      this.updateLastActivity();
    }
  }

  updateLastActivity(time = Math.floor(Date.now() / 1000)) {
    this.lastActivity$.next(time);
  }

  useLightTheme() {
    this.store.dispatch(AuthActions.ChangeTheme({ theme: 'light' }));
  }

  useDarkTheme() {
    this.store.dispatch(AuthActions.ChangeTheme({ theme: 'dark' }));
  }

  toggleTheme() {
    this.theme$.pipe(take(1)).subscribe((theme: string) => {
      if (theme === 'light') {
        this.useDarkTheme();
      } else {
        this.useLightTheme();
      }
    });
  }

  setTheme(theme: string) {
    const link: HTMLElement = document.getElementById('theme-link');

    if (link.getAttribute('href').includes(theme)) {
      return;
    }

    link.setAttribute('href', link.getAttribute('href').replace(/light|dark/gi, theme));
  }

  timeoutDialog(config: DynamicDialogConfig = {}): DynamicDialogRef {
    if (this.timeoutDialogOpen) {
      return null;
    }

    return this.dialogService.open(TimeoutComponent, {
      ...dialogAttributes,
      header: this.translate('auth.timeout.dialog.title'),
      styleClass: 'lookup-dialog timeout-dialog',
      ...config,
      closeOnEscape: false,
      closable: false,
      resizable: false,
      draggable: false,
    });
  }

  checkPermission(access: string): boolean {
    let result: string = 'ACCESSDENIED';

    if (!this.permissions || this.permissions.length === 0) {
      if (this.groupid === 'SUPER') {
        result = 'SUPERVISOR';
      }
    } else {
      for (const permission of this.permissions) {
        // check formid + facility first
        if (permission.formid === access && permission.facility === this.facility) {
          result = permission.setting;
          break;
        }

        // check formid for user / group only second
        if (permission.formid === access) {
          result = permission.setting;
          break;
        }

        // return supervisor permission when not explicitly specified above and part of SUPER group
        if (this.groupid === 'SUPER') {
          result = 'SUPERVISOR';
          break;
        }
      }
    }

    // console.log(`checkPermission [requested: ${access}] [result: ${result}]; returning`, result === 'ACCESSDENIED' ? false : true);

    if (result === 'ACCESSDENIED') {
      return false;
    } else {
      return true;
    }
  }

  // if any one permission provided returns `true` the check has passed
  checkPermissionAny(access: string[]): boolean {
    for (const permission of access) {
      if (this.checkPermission(permission)) {
        return true;
      }
    }

    return false;
  }

  // if all permissions provided return `true` the check has passed
  checkPermissionAll(access: string[]): boolean {
    let pass = 0;

    for (const permission of access) {
      if (this.checkPermission(permission)) {
        pass++;
      }
    }

    if (pass === access.length) {
      return true;
    } else {
      return false;
    }
  }
}
